import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fifoService } from '../../services/fifoService';
import { toast } from 'react-toastify';
import PageLoader from '../../components/PageLoader';

const JOB_OPTIONS = [
    "ทดสอบเครื่องล้างภาชนะ (ใหม่)",
    "ทดสอบเครื่องล้างภาชนะ (เก่า)",
    "ทดสอบเครื่องล้างภาชนะ (เก่า) Repair and Modify",
    "ตรวจสอบอุปกรณ์แถมภายในเครื่อง",
    "ทดสอบเครื่องทำความสะอาดด้านพื้น",
    "ทดสอบใช้งานโมบาย",
    "ทดสอบเครื่องจักรอุปกรณ์",
    "อื่นๆ"
];

const EditRequest = () => {
    const { document_id } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [notEditable, setNotEditable] = useState(null); // holds status string if not editable

    const [requestData, setRequestData] = useState({
        customerName: '',
        customerAddress: '',
        wantDate: '',
        qcmDate: '',
        remark: '',
        departmentExpense: '',
        jobDetails: [],
        otherJobDetail: ''
    });

    // Items already saved on this document — removing one here just marks it
    // for deletion on save, which frees that serial for other requests.
    const [existingItems, setExistingItems] = useState([]);
    const [removedSerials, setRemovedSerials] = useState([]);

    // Items being added, picked the same way as on the create-request page.
    const [newCart, setNewCart] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState(null);
    const [searching, setSearching] = useState(false);
    const [showSearch, setShowSearch] = useState(false);

    useEffect(() => {
        fetchDocument();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [document_id]);

    const fetchDocument = async () => {
        setLoading(true);
        try {
            const data = await fifoService.getEditableRequest(document_id);
            if (!data.editable) {
                setNotEditable(data.status);
                return;
            }
            setRequestData({
                customerName: data.customerName || '',
                customerAddress: data.customerAddress || '',
                wantDate: data.wantDate || '',
                qcmDate: data.qcmDate || '',
                remark: data.remark || '',
                departmentExpense: data.departmentExpense || '',
                jobDetails: data.requestDetails?.options || [],
                otherJobDetail: data.requestDetails?.other || ''
            });
            setExistingItems(data.items || []);
            setRemovedSerials([]);
        } catch (error) {
            toast.error('ไม่พบเอกสารนี้');
            navigate('/all-requests');
        } finally {
            setLoading(false);
        }
    };

    const toggleJobDetail = (option) => {
        const current = requestData.jobDetails;
        if (current.includes(option)) {
            setRequestData({ ...requestData, jobDetails: current.filter(o => o !== option) });
        } else {
            setRequestData({ ...requestData, jobDetails: [...current, option] });
        }
    };

    // existingItems holds the document's full original item list from the
    // server and is never mutated — "removing" an item just adds its serial
    // to removedSerials, and the visible list below is existingItems minus
    // whatever's in removedSerials. That makes undo trivial (drop it back out
    // of removedSerials) without needing to re-fetch and risk clobbering any
    // other in-progress edits (customer name, dates, etc.) the user has made.
    const visibleExistingItems = existingItems.filter(i => !removedSerials.includes(i.serial));

    const handleRemoveExisting = (serial) => {
        setRemovedSerials([...removedSerials, serial]);
    };

    const handleUndoRemove = (serial) => {
        setRemovedSerials(removedSerials.filter(s => s !== serial));
    };

    const handleSearch = async (e) => {
        e?.preventDefault();
        setSearching(true);
        try {
            const data = await fifoService.searchProducts(searchQuery ? { search: searchQuery } : {});
            setSearchResults(data);
        } catch (error) {
            toast.error('ไม่สามารถค้นหาสินค้าได้');
        } finally {
            setSearching(false);
        }
    };

    const addToNewCart = (serialItem) => {
        if (newCart.find(i => i.serial === serialItem.serial)) {
            toast.warning('สินค้านี้ถูกเลือกแล้ว');
            return;
        }
        setNewCart([...newCart, serialItem]);
    };

    const removeFromNewCart = (serial) => {
        setNewCart(newCart.filter(i => i.serial !== serial));
    };

    const totalRemainingItems = visibleExistingItems.length + newCart.length;

    const handleSave = async () => {
        if (!requestData.customerName || !requestData.wantDate) {
            toast.error('กรุณาระบุชื่อลูกค้าและวันที่ต้องการใช้เครื่อง');
            return;
        }
        if (totalRemainingItems < 1) {
            toast.error('เอกสารต้องมีรายการเครื่องอย่างน้อย 1 รายการ');
            return;
        }

        const payload = {
            customerName: requestData.customerName,
            customerAddress: requestData.customerAddress,
            wantDate: requestData.wantDate,
            qcmDate: requestData.qcmDate,
            remark: requestData.remark,
            departmentExpense: requestData.departmentExpense,
            requestDetails: { options: requestData.jobDetails, other: requestData.otherJobDetail },
            removedSerials,
            // The exact serial that was picked — not just "1 unit of this
            // product", which let FIFO silently hand back the serial that had
            // just been removed.
            newItems: newCart.map(item => ({
                productId: item.itemCode,
                serial: item.serial,
                description: item.description
            }))
        };

        setSaving(true);
        try {
            await fifoService.updateRequest(document_id, payload);
            toast.success('บันทึกการแก้ไขสำเร็จ');
            navigate(`/request-detail/${document_id}`);
        } catch (error) {
            if (error.response?.status === 409) {
                toast.error(error.response.data.error);
                navigate(`/request-detail/${document_id}`);
            } else {
                toast.error('บันทึกไม่สำเร็จ: ' + (error.response?.data?.error || error.message));
            }
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <PageLoader message="กำลังโหลดข้อมูล..." />;
    }

    if (notEditable) {
        return (
            <div className="text-center py-5">
                <i className="bi bi-lock-fill display-4 text-muted mb-3 d-block"></i>
                <h4>ไม่สามารถแก้ไขเอกสารนี้ได้</h4>
                <p className="text-muted">
                    เอกสารนี้ถูกดำเนินการโดยคลังสินค้าแล้ว (สถานะปัจจุบัน: {notEditable})
                </p>
                <button className="btn btn-primary" onClick={() => navigate(`/request-detail/${document_id}`)}>
                    กลับไปดูรายละเอียด
                </button>
            </div>
        );
    }

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <button className="btn btn-link text-muted p-0 mb-2" onClick={() => navigate(`/request-detail/${document_id}`)}>
                        <i className="bi bi-arrow-left me-2"></i>
                        กลับไปดูรายละเอียด
                    </button>
                    <h3 className="mb-0">
                        <i className="bi bi-pencil-square me-2 text-primary"></i>
                        แก้ไขใบแจ้งงาน {document_id}
                    </h3>
                </div>
            </div>

            <div className="row">
                <div className="col-md-6 mb-4">
                    <div className="theme-card h-100">
                        <div className="theme-card-header">
                            <h5 className="mb-0"><i className="bi bi-person-fill me-2"></i>ข้อมูลลูกค้า</h5>
                        </div>
                        <div className="theme-card-body">
                            <div className="mb-3">
                                <label className="form-label">ชื่อลูกค้า / ตัวแทนสาขา <span className="text-danger">*</span></label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={requestData.customerName}
                                    onChange={e => setRequestData({ ...requestData, customerName: e.target.value })}
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label">ที่อยู่ / สถานที่ติดตั้ง</label>
                                <textarea
                                    className="form-control"
                                    rows="2"
                                    value={requestData.customerAddress}
                                    onChange={e => setRequestData({ ...requestData, customerAddress: e.target.value })}
                                ></textarea>
                            </div>
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <label className="form-label">วันที่ต้องการใช้เครื่อง <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="DD/MM/YYYY"
                                        maxLength={10}
                                        value={requestData.wantDate}
                                        onChange={e => {
                                            let v = e.target.value.replace(/[^\d]/g, '');
                                            if (v.length >= 3) v = v.slice(0, 2) + '/' + v.slice(2);
                                            if (v.length >= 6) v = v.slice(0, 5) + '/' + v.slice(5, 9);
                                            setRequestData({ ...requestData, wantDate: v });
                                        }}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">ต้องการรับเครื่องจาก QCM วันที่</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="DD/MM/YYYY"
                                        maxLength={10}
                                        value={requestData.qcmDate}
                                        onChange={e => {
                                            let v = e.target.value.replace(/[^\d]/g, '');
                                            if (v.length >= 3) v = v.slice(0, 2) + '/' + v.slice(2);
                                            if (v.length >= 6) v = v.slice(0, 5) + '/' + v.slice(5, 9);
                                            setRequestData({ ...requestData, qcmDate: v });
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="mt-3">
                                <label className="form-label">เก็บค่าใช้จ่ายที่</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={requestData.departmentExpense}
                                    onChange={e => setRequestData({ ...requestData, departmentExpense: e.target.value })}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-md-6 mb-4">
                    <div className="theme-card h-100">
                        <div className="theme-card-header">
                            <h5 className="mb-0"><i className="bi bi-list-check me-2"></i>รายละเอียดการแจ้งงาน</h5>
                        </div>
                        <div className="theme-card-body">
                            <div className="row g-2 mb-3">
                                {JOB_OPTIONS.map((option, idx) => (
                                    <div key={idx} className="col-md-6">
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id={`job-${idx}`}
                                                checked={requestData.jobDetails.includes(option)}
                                                onChange={() => toggleJobDetail(option)}
                                            />
                                            <label className="form-check-label" htmlFor={`job-${idx}`}>{option}</label>
                                        </div>
                                    </div>
                                ))}
                                {requestData.jobDetails.includes("อื่นๆ") && (
                                    <div className="col-12 mt-2">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="ระบุรายละเอียดอื่นๆ..."
                                            value={requestData.otherJobDetail}
                                            onChange={e => setRequestData({ ...requestData, otherJobDetail: e.target.value })}
                                        />
                                    </div>
                                )}
                            </div>
                            <label className="form-label">หมายเหตุเพิ่มเติม</label>
                            <textarea
                                className="form-control"
                                rows="2"
                                value={requestData.remark}
                                onChange={e => setRequestData({ ...requestData, remark: e.target.value })}
                            ></textarea>
                        </div>
                    </div>
                </div>
            </div>

            {/* ITEMS */}
            <div className="theme-card mb-4">
                <div className="theme-card-header d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                        <i className="bi bi-box-seam me-2"></i>
                        รายการเครื่อง ({totalRemainingItems} รายการ)
                    </h5>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => { setShowSearch(!showSearch); if (!searchResults) handleSearch(); }}
                    >
                        <i className="bi bi-plus-lg me-1"></i>เพิ่มรายการ
                    </button>
                </div>
                <div className="theme-card-body p-0">
                    <div className="table-responsive">
                        <table className="table theme-table mb-0">
                            <thead className="bg-light">
                                <tr>
                                    <th>Serial Number</th>
                                    <th>รหัสสินค้า</th>
                                    <th>รายละเอียด</th>
                                    <th className="text-end">ดำเนินการ</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleExistingItems.map((item, idx) => (
                                    <tr key={`existing-${idx}`}>
                                        <td className="fw-medium">
                                            <i className="bi bi-upc-scan me-2 text-primary"></i>{item.serial}
                                        </td>
                                        <td className="text-muted small">{item.productId}</td>
                                        <td className="text-muted">{item.description || '-'}</td>
                                        <td className="text-end">
                                            <button className="btn btn-sm btn-outline-danger" onClick={() => handleRemoveExisting(item.serial)}>
                                                <i className="bi bi-trash me-1"></i>ลบ
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {newCart.map((item, idx) => (
                                    <tr key={`new-${idx}`} className="table-success">
                                        <td className="fw-medium">
                                            <i className="bi bi-plus-circle me-2 text-success"></i>{item.serial}
                                            <span className="badge bg-success ms-2">ใหม่</span>
                                        </td>
                                        <td className="text-muted small">{item.itemCode}</td>
                                        <td className="text-muted">{item.description || '-'}</td>
                                        <td className="text-end">
                                            <button className="btn btn-sm btn-outline-secondary" onClick={() => removeFromNewCart(item.serial)}>
                                                <i className="bi bi-x-lg"></i>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {totalRemainingItems === 0 && (
                                    <tr>
                                        <td colSpan="4" className="text-center py-4 text-muted">
                                            ไม่มีรายการเครื่อง — ต้องมีอย่างน้อย 1 รายการก่อนบันทึก
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {removedSerials.length > 0 && (
                        <div className="p-3 border-top bg-light">
                            <div className="small text-muted mb-2">รายการที่ถูกลบ (จะถูกปล่อยคืนสต็อกเมื่อบันทึก)</div>
                            <div className="d-flex flex-wrap gap-2">
                                {removedSerials.map((serial, idx) => (
                                    <span key={idx} className="badge bg-secondary d-flex align-items-center gap-2 p-2">
                                        <s>{serial}</s>
                                        <button
                                            className="btn btn-sm btn-link text-white p-0"
                                            style={{ textDecoration: 'none', fontSize: '14px' }}
                                            onClick={() => handleUndoRemove(serial)}
                                            title="เลิกทำ"
                                        >
                                            <i className="bi bi-arrow-counterclockwise"></i>
                                        </button>
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* ADD ITEM SEARCH */}
            {showSearch && (
                <div className="theme-card mb-4">
                    <div className="theme-card-header">
                        <h5 className="mb-0"><i className="bi bi-search me-2"></i>ค้นหาสินค้าเพื่อเพิ่ม</h5>
                    </div>
                    <div className="theme-card-body">
                        <form onSubmit={handleSearch} className="mb-3">
                            <div className="row g-3">
                                <div className="col">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="ค้นหาด้วยชื่อสินค้า, รหัสสินค้า หรือซีเรียล..."
                                        value={searchQuery}
                                        onChange={e => setSearchQuery(e.target.value)}
                                    />
                                </div>
                                <div className="col-auto">
                                    <button type="submit" className="btn btn-primary" disabled={searching}>
                                        <i className="bi bi-search me-2"></i>ค้นหา
                                    </button>
                                </div>
                            </div>
                        </form>
                        <div className="table-responsive" style={{ maxHeight: '400px' }}>
                            <table className="table theme-table table-hover mb-0">
                                <thead className="bg-light sticky-top">
                                    <tr>
                                        <th>Serial</th>
                                        <th>รหัสสินค้า</th>
                                        <th>รายละเอียด</th>
                                        <th>สถานะ</th>
                                        <th className="text-end">เลือก</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {searching && (
                                        <tr><td colSpan="5" className="text-center py-4">
                                            <div className="spinner-border spinner-border-sm text-primary"></div>
                                        </td></tr>
                                    )}
                                    {!searching && searchResults?.remainingSerialsDetails?.map((item, idx) => {
                                        const isAdded = newCart.some(c => c.serial === item.serial);
                                        return (
                                            <tr key={idx}>
                                                <td className="fw-medium">{item.serial}</td>
                                                <td className="text-muted small">{item.itemCode}</td>
                                                <td className="text-truncate" style={{ maxWidth: '280px' }}>{item.description}</td>
                                                <td>
                                                    <span className={`badge rounded-pill ${item.status === 'Available' ? 'bg-success' : 'bg-secondary'}`}>
                                                        {item.status === 'Available' ? 'ว่าง' : item.status}
                                                    </span>
                                                </td>
                                                <td className="text-end">
                                                    {item.status === 'Available' ? (
                                                        <button
                                                            className={`btn btn-sm ${isAdded ? 'btn-success' : 'btn-outline-primary'}`}
                                                            onClick={() => addToNewCart(item)}
                                                            disabled={isAdded}
                                                        >
                                                            {isAdded ? <i className="bi bi-check-lg"></i> : <i className="bi bi-plus-lg"></i>}
                                                        </button>
                                                    ) : <span className="text-muted small">-</span>}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            <div className="text-end mb-5">
                <button className="btn btn-secondary me-2" onClick={() => navigate(`/request-detail/${document_id}`)}>
                    ยกเลิก
                </button>
                <button className="btn btn-success btn-lg px-5" onClick={handleSave} disabled={saving}>
                    {saving ? <span className="spinner-border spinner-border-sm me-2"></span> : <i className="bi bi-save me-2"></i>}
                    {saving ? 'กำลังบันทึก...' : 'บันทึกการแก้ไข'}
                </button>
            </div>
        </div>
    );
};

export default EditRequest;
